import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Send, Phone, Truck, ArrowLeft, Package } from "lucide-react";
import axios from "axios";
import "./ContactCourier.css";
import cacheStorage from '../../../../utils/cacheStorage';

const API_BASE = "https://smart-cargo-backend-923182991420.us-central1.run.app";

const formatMessageTime = (value) => {
  if (!value) return "";

  const normalized =
      typeof value === "string" && value.includes(" ") && !value.includes("T")
          ? value.replace(" ", "T")
          : value;
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const createTempMessage = (senderType, content) => ({
  id: `temp-${Date.now()}-${Math.random()}`,
  sender_type: senderType,
  content,
  created_at: new Date().toISOString(),
  pending: true,
});


export function ContactCourier() {
  const { courierId } = useParams();
  const navigate = useNavigate();

  const [courier, setCourier] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [showAssignments, setShowAssignments] = useState(false);
  const [wsReady, setWsReady] = useState(false);

  const ws = useRef(null);
  const bottomRef = useRef(null);
  const reconnectTimer = useRef(null);

  useEffect(() => {
    const token = cacheStorage.getItem("access_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    axios
        .get(`${API_BASE}/api/admin/couriers`, { headers })
        .then(({ data }) => {
          const found = data.find((c) => String(c.id) === String(courierId));
          if (found) {
            setCourier(found);
          }
        })
        .catch(console.error);
  }, [courierId]);

  useEffect(() => {
    if (!courierId) return;

    const token = cacheStorage.getItem("access_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    axios
        .get(`${API_BASE}/api/messages?courier_id=${courierId}`, { headers })
        .then(({ data }) => setMessages(Array.isArray(data) ? data : []))
        .catch(console.error);
  }, [courierId]);

  const connectWS = useCallback(() => {
    if (!courierId) return;

    const token = cacheStorage.getItem("access_token");
    const params = new URLSearchParams({ courier_id: courierId });

    if (token) {
      params.set("token", token);
    }

    const socket = new WebSocket(`${API_BASE.replace("http", "ws")}/api/messages?${params.toString()}`);

    socket.onopen = () => {
      setWsReady(true);
      clearTimeout(reconnectTimer.current);
    };
    socket.onclose = () => {
      setWsReady(false);
      reconnectTimer.current = setTimeout(connectWS, 3000);
    };
    socket.onerror = () => socket.close();
    socket.onmessage = (e) => {
      const msg = JSON.parse(e.data);

      setMessages((prev) => {
        const next = [...prev];

        const tempIndex = next.findIndex(
            (message) =>
                String(message.id).startsWith("temp-") &&
                message.pending &&
                message.sender_type === msg.sender_type &&
                message.content === msg.content
        );

        if (tempIndex !== -1) {
          next[tempIndex] = msg;
          return next;
        }

        return next.some((message) => message.id === msg.id)
            ? next
            : [...next, msg];
      });
    };

    ws.current = socket;
  }, [courierId]);

  useEffect(() => {
    connectWS();

    return () => {
      clearTimeout(reconnectTimer.current);
      ws.current?.close();
    };
  }, [connectWS]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    const text = newMessage.trim();
    if (!text || !wsReady || !ws.current) return;

    const tempMessage = createTempMessage("admin", text);

    setMessages((prev) => [...prev, tempMessage]);
    ws.current.send(JSON.stringify({ sender_type: "admin", content: text }));
    setNewMessage("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const courierName = courier?.name ?? "Courier";

  return (
      <div className="contact-courier-container">
        <div className="contact-courier-header">
          <button
              onClick={() => navigate("/admin/couriers")}
              className="back-button"
          >
            <ArrowLeft className="icon" />
          </button>
          <div className="header-text">
            <h1 className="title">Contact Courier</h1>
            <p className="subtitle">Communicate with {courierName}</p>
          </div>
        </div>

        <div className="contact-courier-grid">
          <div className="chat-section">
            <div className="chat-header">
              <div className="courier-profile">
                <div className="avatar">{courierName.charAt(0)}</div>
                <div className="profile-info">
                  <h3>{courierName}</h3>
                  <div className="status">
                    <div
                        className="status-dot"
                        style={{
                          backgroundColor: wsReady ? "#10B981" : "#94a3b8",
                        }}
                    ></div>
                    <span>{wsReady ? "Online" : "Connecting..."}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="chat-messages">
              {messages.map((msg) => (
                  <div
                      key={msg.id}
                      className={`message-wrapper ${
                          msg.sender_type === "admin"
                              ? "message-admin"
                              : "message-courier"
                      }`}
                  >
                    <div className="message-bubble">
                      <p className="message-text">{msg.content}</p>
                      <p className="message-time">
                        {formatMessageTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <div className="chat-input-area">
              <div className="input-wrapper">
                <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={wsReady ? "Type a message..." : "Connecting..."}
                    disabled={!wsReady}
                    className="message-input"
                />
                <button
                    onClick={handleSend}
                    disabled={!wsReady}
                    className="send-button"
                >
                  <Send className="icon" />
                </button>
              </div>
            </div>
          </div>

          <div className="sidebar-section">
            <div className="info-card">
              <h3>Contact Information</h3>
              <div className="info-list">
                <div className="info-item">
                  <Phone className="icon" />
                  <div className="info-details">
                    <p className="label">Phone</p>
                    <p className="value">{courier?.phone ?? "-"}</p>
                  </div>
                </div>
                <div className="info-item">
                  <Truck className="icon" />
                  <div className="info-details">
                    <p className="label">Vehicle</p>
                    <p className="value">{courier?.vehicle ?? "-"}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="actions-card">
              <h3>Quick Actions</h3>
              <div className="action-buttons">
                <a href={`tel:${courier?.phone ?? ""}`} className="btn-primary">
                  Call Courier
                </a>
                <button className="btn-secondary">View Route</button>
                <button
                    className="btn-secondary"
                    onClick={() => setShowAssignments(!showAssignments)}
                >
                  View Assignments
                </button>
              </div>
            </div>

            {showAssignments && (
                <div className="assignments-card">
                  <h3>Assigned Packages</h3>
                  <div className="info-item">
                    <Package className="icon" />
                    <div className="info-details">
                      <p className="label">Total Deliveries</p>
                      <p className="value">{courier?.deliveries ?? 0}</p>
                    </div>
                  </div>
                </div>
            )}
          </div>
        </div>
      </div>
  );
}
